/**
 * ---------------------------------------------------------------------------------------------------------------------
 * CUSTOM PDP
 * ---------------------------------------------------------------------------------------------------------------------
 * Behaviour for the "Custom PDP" section (sections/pdp-custom.liquid).
 *
 * Custom elements defined here:
 *
 * - <pdp-gallery>           : product media carousel with thumbnails and arrows
 * - <pdp-video>             : click-to-play video (Shopify-hosted, YouTube, Vimeo or a direct file)
 * - <pdp-purchase-options>  : one-time / subscription selector, writes "id" and "selling_plan" on the product form
 * - <pdp-offer>             : quantity deal picker, writes "id" and "quantity" and unlocks the free gifts it comes with
 * - <pdp-quantity>          : quantity stepper
 * - <pdp-cart-progress>     : live "spent so far" progress bar, reads the cart
 * - <pdp-gift-tier>         : gift card that unlocks once the cart reaches its threshold
 *
 * The add-to-cart form itself is handled by the theme's own <product-form> element, so adding to cart keeps opening the
 * cart drawer and refreshing the cart count exactly like the rest of the theme.
 */

(function () {
  'use strict';

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------------------------------------------------------
     MONEY FORMATTING
     ------------------------------------------------------------------------------------------------------------------ */

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number === null) return '0';

    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(cents) {
    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    const format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat || settings.moneyFormat || '${{amount}}'
      : settings.moneyFormat || '${{amount}}';

    if (typeof cents === 'string') cents = cents.replace('.', '');

    const value = Number(cents);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);

    if (!match) return format;

    let formatted;

    switch (match[1]) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    return format.replace(placeholderRegex, formatted);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     CART TOTAL WATCHER
     ------------------------------------------------------------------------------------------------------------------
     A single shared source of truth for "how much is currently in the cart", so the progress bar and every gift tier
     stay in sync without each of them hitting /cart.js on their own. */

  let currentTotal = null;
  let pendingFetch = null;

  function totalFromCart(cart) {
    if (!cart) return null;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    if (typeof cart.total_price === 'number') return cart.total_price;
    return null;
  }

  function broadcast(total) {
    if (total === null || total === currentTotal) return;

    currentTotal = total;
    notify();
  }

  function fetchCartTotal() {
    if (pendingFetch) return pendingFetch;

    pendingFetch = fetch(`${rootUrl}cart.js`)
      .then((response) => response.json())
      .then((cart) => broadcast(totalFromCart(cart)))
      .catch(() => {
        /* Network errors leave the server-rendered state in place. */
      })
      .finally(() => {
        pendingFetch = null;
      });

    return pendingFetch;
  }

  document.addEventListener('cart:change', (event) => {
    const total = totalFromCart(event.detail && event.detail.cart);

    if (total === null) {
      fetchCartTotal();
    } else {
      broadcast(total);
    }
  });

  document.addEventListener('cart:refresh', () => fetchCartTotal());

  /* ------------------------------------------------------------------------------------------------------------------
     PROJECTED TOTAL
     ------------------------------------------------------------------------------------------------------------------
     The gift progress bar and the gift tiers show what the buyer will have spent once they add this product, so the
     bar fills and gifts unlock live while the quantity or the purchase option changes — the gifts themselves are then
     added to the cart by the gift app.

         projected = what is already in the cart + (unit price of the selected option x chosen quantity)
  */

  const consumers = new Set();

  function sectionRoot(element) {
    return element.closest('.pdp') || document;
  }

  /* Price in cents of one unit of the currently selected purchase option or offer deal */
  function selectedUnitPrice(root) {
    const checked = root.querySelector(
      'pdp-purchase-options input[type="radio"]:checked, pdp-offer input[type="radio"]:checked'
    );

    if (checked) {
      const frequency = root.querySelector(`[data-pdp-selling-plan-frequency="${checked.value}"]`);
      const selectedFrequency = frequency && frequency.options[frequency.selectedIndex];
      const price = parseInt(
        (selectedFrequency && selectedFrequency.dataset.priceCents) || checked.dataset.priceCents,
        10
      );

      if (Number.isFinite(price)) return price;
    }

    // No purchase options block on the page — fall back to the price the section rendered on its own root
    const fallback =
      root.dataset && root.dataset.pdpUnitPrice !== undefined
        ? root
        : root.querySelector('[data-pdp-unit-price]');
    const fallbackPrice = fallback ? parseInt(fallback.dataset.pdpUnitPrice, 10) : NaN;

    return Number.isFinite(fallbackPrice) ? fallbackPrice : 0;
  }

  function selectedQuantity(root) {
    const input = root.querySelector('input[name="quantity"]');

    if (!input) return 1;

    const quantity = parseInt(input.value, 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  function projectedTotal(root) {
    return (currentTotal || 0) + selectedUnitPrice(root) * selectedQuantity(root);
  }

  /* Re-render every consumer, or only those belonging to one section when a change is local to it */
  function notify(root) {
    consumers.forEach((element) => {
      const elementRoot = sectionRoot(element);

      if (root && elementRoot !== root) return;

      element.renderTotal(projectedTotal(elementRoot));
    });
  }

  function registerConsumer(element) {
    consumers.add(element);
    element.renderTotal(projectedTotal(sectionRoot(element)));

    if (currentTotal === null) fetchCartTotal();

    return () => consumers.delete(element);
  }

  /* The quantity stepper re-dispatches "change" on its input, so both events are covered here */
  const projectionInputs =
    'input[name="quantity"], pdp-purchase-options input[type="radio"], pdp-offer input[type="radio"], [data-pdp-selling-plan-frequency]';

  const priceInputs =
    'pdp-purchase-options input[type="radio"], pdp-offer input[type="radio"], [data-pdp-selling-plan-frequency]';

  function onProjectionInput(event) {
    const target = event.target;

    if (target && target.matches && target.matches(projectionInputs)) {
      notify(sectionRoot(target));
    }
  }

  document.addEventListener('change', onProjectionInput);
  document.addEventListener('input', onProjectionInput);

  /* ------------------------------------------------------------------------------------------------------------------
     DEAL DISCOUNT CODES
     ------------------------------------------------------------------------------------------------------------------
     The offer block applies a deal's discount code to the cart right after the add, but the drawer opens at the same
     time: a buyer who clicks checkout straight away navigates before that request is done. So the codes are also
     remembered and sent with the checkout submit itself as the "discount" field of the cart form, which Shopify
     carries over to checkout. sessionStorage keeps them when the buyer goes through the cart page instead. */

  const discountStorageKey = 'pdp-deal-discount-codes';

  function storedDiscountCodes() {
    try {
      return JSON.parse(sessionStorage.getItem(discountStorageKey)) || [];
    } catch (error) {
      return [];
    }
  }

  function rememberDiscountCodes(codes) {
    try {
      sessionStorage.setItem(discountStorageKey, JSON.stringify(codes));
    } catch (error) {
      // Storage can be blocked (private mode): the cart request alone still applies the code
    }
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const submitter = event.submitter;
      const codes = storedDiscountCodes();

      if (!codes.length || !submitter || submitter.name !== 'checkout') return;

      let input = form.querySelector('input[name="discount"][data-pdp-deal-discount]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.setAttribute('data-pdp-deal-discount', '');
        form.appendChild(input);
      }

      input.value = codes.join(',');
    },
    true
  );

  /* ------------------------------------------------------------------------------------------------------------------
     FORM ERRORS
     ------------------------------------------------------------------------------------------------------------------
     The theme's <product-form> element dispatches "cart:error" on the form when Shopify rejects the add to cart
     (out of stock, quantity rule...). We surface it right under the buy buttons. */

  document.addEventListener('cart:error', (event) => {
    const holder = event.target && event.target.querySelector && event.target.querySelector('[data-pdp-form-error]');

    if (holder) holder.textContent = (event.detail && event.detail.error) || '';
  });

  document.addEventListener('cart:change', () => {
    document.querySelectorAll('[data-pdp-form-error]').forEach((holder) => {
      holder.textContent = '';
    });
  });

  /* ------------------------------------------------------------------------------------------------------------------
     GALLERY
     ------------------------------------------------------------------------------------------------------------------ */

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-pdp-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-pdp-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pdp-gallery-thumb]'));
      this.previousButton = this.querySelector('[data-pdp-gallery-prev]');
      this.nextButton = this.querySelector('[data-pdp-gallery-next]');
      this.index = 0;

      if (!this.viewport || this.slides.length === 0) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.select(index), { signal });
      });

      if (this.previousButton) {
        this.previousButton.addEventListener('click', () => this.select(this.index - 1), { signal });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.select(this.index + 1), { signal });
      }

      // Keep the active thumbnail in sync when the buyer swipes the gallery directly.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const index = this.slides.indexOf(entry.target);

            if (index !== -1) this.setActive(index);
          });
        },
        { root: this.viewport, threshold: 0.6 }
      );

      this.slides.forEach((slide) => this.observer.observe(slide));
      this.setActive(0, false);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.observer) this.observer.disconnect();
    }

    select(index) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[clamped];

      if (!slide) return;

      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
      this.setActive(clamped);
    }

    setActive(index, revealThumb = true) {
      this.index = index;

      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === index;

        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (isActive && revealThumb) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });

      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     VIDEO
     ------------------------------------------------------------------------------------------------------------------
     Handles the click-to-play poster. YouTube/Vimeo embeds live in a <template> until the buyer presses play, so no
     third-party iframe is loaded on page view. */

  class PdpVideo extends HTMLElement {
    connectedCallback() {
      this.poster = this.querySelector('[data-pdp-video-play]');

      if (!this.poster) return;

      this.abortController = new AbortController();
      this.poster.addEventListener('click', () => this.play(), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    play() {
      const embed = this.querySelector('[data-pdp-video-embed]');
      const template = embed && embed.querySelector('template');

      if (template) {
        embed.appendChild(template.content.cloneNode(true));
        template.remove();
      }

      const video = this.querySelector('video');

      if (video) {
        const playback = video.play();

        // Safari rejects unmuted programmatic playback in some contexts — fall back to a muted start.
        if (playback && typeof playback.catch === 'function') {
          playback.catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      }

      this.classList.add('is-playing');
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     PURCHASE OPTIONS
     ------------------------------------------------------------------------------------------------------------------ */

  class PdpPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();

      this.addEventListener('change', () => this.update(), { signal: this.abortController.signal });
      this.update();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    get form() {
      const formId = this.getAttribute('form');

      return (formId && document.getElementById(formId)) || this.closest('form');
    }

    update() {
      const checked = this.querySelector('input[type="radio"]:checked');

      this.querySelectorAll('[data-pdp-option]').forEach((option) => {
        option.classList.toggle('is-selected', option.contains(checked));
      });

      if (!checked) return;

      const form = this.form;

      if (!form) return;

      const variantInput = form.querySelector('input[name="id"]');
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');

      if (variantInput && checked.dataset.variantId) {
        variantInput.value = checked.dataset.variantId;
      }

      const frequency = this.querySelector(`[data-pdp-selling-plan-frequency="${checked.value}"]`);

      if (sellingPlanInput) {
        // A one-time purchase must not send an empty selling_plan, so the input is disabled instead.
        const sellingPlanId = (frequency && frequency.value) || checked.dataset.sellingPlan || '';

        sellingPlanInput.value = sellingPlanId;
        sellingPlanInput.disabled = sellingPlanId === '';
      }

      // Keep the displayed price in sync with the chosen delivery frequency.
      if (frequency) {
        const selectedFrequency = frequency.options[frequency.selectedIndex];
        const option = frequency.closest('[data-pdp-option]');

        if (selectedFrequency && option) {
          const price = option.querySelector('[data-pdp-option-price]');
          const discount = option.querySelector('[data-pdp-option-discount]');

          if (price && selectedFrequency.dataset.price) {
            price.textContent = selectedFrequency.dataset.price;
          }

          if (discount && selectedFrequency.dataset.discount) {
            discount.textContent = selectedFrequency.dataset.discount;
          }
        }
      }

      this.dispatchEvent(
        new CustomEvent('pdp:purchase-option:change', {
          bubbles: true,
          detail: {
            variantId: checked.dataset.variantId,
            sellingPlanId: checked.dataset.sellingPlan || null
          }
        })
      );
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     OFFER DEAL
     ------------------------------------------------------------------------------------------------------------------
     "Buy one / buy two / buy three" deal picker. Picking a deal writes its variant and its quantity on the product
     form, and unlocks every gift whose unlock quantity it reaches. */

  class PdpOffer extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();

      this.addEventListener('change', () => this.update(), { signal: this.abortController.signal });
      document.addEventListener('variant:add', (event) => this.applyDiscount(event), {
        signal: this.abortController.signal
      });
      this.update();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    get form() {
      const formId = this.getAttribute('form');

      return (formId && document.getElementById(formId)) || this.closest('form');
    }

    update() {
      const checked = this.querySelector('input[type="radio"]:checked');

      this.querySelectorAll('[data-pdp-deal-tier]').forEach((tier) => {
        tier.classList.toggle('is-selected', tier.contains(checked));
      });

      if (!checked) return;

      const quantity = parseInt(checked.dataset.quantity, 10) || 1;
      const position = (parseInt(checked.value, 10) || 0) + 1;

      // A gift either lists the deal positions that unlock it (",1,3,") or unlocks from a minimum quantity
      this.querySelectorAll('[data-pdp-deal-gift]').forEach((gift) => {
        const unlockDeals = gift.dataset.unlockDeals;
        const unlockQuantity = parseInt(gift.dataset.unlockQuantity, 10) || 1;
        const unlocked =
          unlockDeals !== undefined ? unlockDeals.includes(`,${position},`) : quantity >= unlockQuantity;

        gift.classList.toggle('is-unlocked', unlocked);
      });

      const form = this.form;

      if (!form) return;

      const variantInput = form.querySelector('input[name="id"]');
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]');

      if (variantInput && checked.dataset.variantId) {
        variantInput.value = checked.dataset.variantId;
      }

      // Deals are one-time purchases: an empty selling_plan must not be submitted, so the input is disabled instead.
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }

      /**
       * Either the stepper of the buy buttons block or the hidden input this block renders when there is no stepper.
       * The guard keeps the dispatched "change" from bouncing back here in an endless loop when the input is our own.
       */
      const quantityInput = form.querySelector('input[name="quantity"]');

      if (quantityInput && quantityInput.value !== String(quantity)) {
        quantityInput.value = String(quantity);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.dispatchEvent(
        new CustomEvent('pdp:offer:change', {
          bubbles: true,
          detail: { variantId: checked.dataset.variantId, quantity: quantity }
        })
      );
    }

    /**
     * The cart AJAX API has no way to add a discount code along with the item, so it is applied right after the theme's
     * product form reports a successful add. The codes already on the cart are kept, and the drawer is refreshed so it
     * shows the discounted totals.
     */
    async applyDiscount(event) {
      if (event.target !== this.form) return;

      const checked = this.querySelector('input[type="radio"]:checked');
      const code = checked && checked.dataset.discountCode;

      if (!code) return;

      const cart = (event.detail && event.detail.cart) || {};
      const codes = (cart.discount_codes || []).map((discount) => discount.code);

      if (codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        rememberDiscountCodes(codes);
        return;
      }

      const nextCodes = codes.concat(code);

      rememberDiscountCodes(nextCodes);

      try {
        const response = await fetch(`${rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: nextCodes.join(',') })
        });

        if (!response.ok) console.warn('[pdp-offer] The discount code could not be applied to the cart', code);
      } finally {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     QUANTITY
     ------------------------------------------------------------------------------------------------------------------ */

  class PdpQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');

      if (!this.input) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.querySelectorAll('[data-pdp-quantity-step]').forEach((button) => {
        button.addEventListener(
          'click',
          () => this.step(parseInt(button.dataset.pdpQuantityStep, 10) || 1),
          { signal }
        );
      });

      this.input.addEventListener('change', () => this.sync(), { signal });
      this.sync();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    step(direction) {
      const increment = parseInt(this.input.step, 10) || 1;

      this.input.value = String((parseInt(this.input.value, 10) || 0) + increment * direction);
      this.sync();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
      const min = parseInt(this.input.min, 10) || 1;
      const max = this.input.max === '' ? Infinity : parseInt(this.input.max, 10);
      let value = parseInt(this.input.value, 10);

      if (isNaN(value)) value = min;

      value = Math.max(min, Math.min(value, max));
      this.input.value = String(value);

      const decrease = this.querySelector('[data-pdp-quantity-step="-1"]');
      const increase = this.querySelector('[data-pdp-quantity-step="1"]');

      if (decrease) decrease.disabled = value <= min;
      if (increase) increase.disabled = value >= max;
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     CART PROGRESS
     ------------------------------------------------------------------------------------------------------------------ */

  class PdpCartProgress extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('[data-pdp-progress-fill]');
      this.value = this.querySelector('[data-pdp-progress-value]');
      this.goal = parseInt(this.getAttribute('goal'), 10) || 0;
      this.template = this.getAttribute('value-template') || '{{ amount }}';

      this.unregister = registerConsumer(this);
    }

    disconnectedCallback() {
      if (this.unregister) this.unregister();
    }

    renderTotal(total) {
      const percentage = this.goal > 0 ? Math.min(100, (total / this.goal) * 100) : 0;

      if (this.fill) {
        this.fill.style.width = `${percentage}%`;
      }

      if (this.value) {
        this.value.textContent = this.template.replace('{{ amount }}', formatMoney(total));
      }

      this.setAttribute('aria-valuenow', String(total));
      this.classList.toggle('is-complete', this.goal > 0 && total >= this.goal);
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     GIFT TIER
     ------------------------------------------------------------------------------------------------------------------ */

  class PdpGiftTier extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.getAttribute('threshold'), 10) || 0;
      this.unregister = registerConsumer(this);
    }

    disconnectedCallback() {
      if (this.unregister) this.unregister();
    }

    renderTotal(total) {
      this.classList.toggle('is-unlocked', total >= this.threshold);
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     PRICE
     ------------------------------------------------------------------------------------------------------------------
     The price block is rendered from the product on the server. When a purchase options or an offer block is also on
     the page the picked radio carries the prices of the option, so the block follows the selection. Without one of
     those blocks there is nothing to follow and the rendered price simply stays as it is. */

  class PdpPrice extends HTMLElement {
    connectedCallback() {
      this.current = this.querySelector('[data-pdp-price-current]');
      this.compare = this.querySelector('[data-pdp-price-compare]');
      this.badge = this.querySelector('[data-pdp-price-badge]');
      this.badgeTemplate = this.getAttribute('data-pdp-price-badge-template') || '';

      this.onChange = this.onChange.bind(this);

      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
    }

    onChange(event) {
      const target = event.target;

      if (!target || !target.matches || !target.matches(priceInputs)) return;
      if (sectionRoot(target) !== sectionRoot(this)) return;

      this.render();
    }

    render() {
      const root = sectionRoot(this);
      const checked = root.querySelector(
        'pdp-purchase-options input[type="radio"]:checked, pdp-offer input[type="radio"]:checked'
      );

      if (!checked) return;

      const frequency = root.querySelector(`[data-pdp-selling-plan-frequency="${checked.value}"]`);
      const selectedFrequency = frequency && frequency.options[frequency.selectedIndex];
      const price = parseInt(
        (selectedFrequency && selectedFrequency.dataset.priceCents) || checked.dataset.priceCents,
        10
      );
      const compare = parseInt(checked.dataset.compareCents, 10);

      if (!Number.isFinite(price)) return;

      if (this.current) this.current.textContent = formatMoney(price);

      const hasCompare = Number.isFinite(compare) && compare > price;

      if (this.compare) {
        this.compare.textContent = hasCompare ? formatMoney(compare) : '';
        this.compare.hidden = !hasCompare;
      }

      if (!this.badge) return;

      const discount = hasCompare ? Math.round(((compare - price) / compare) * 100) : 0;

      if (discount > 0 && this.badgeTemplate) {
        this.badge.innerHTML = this.badgeTemplate.replace('[discount]', String(discount));
        this.badge.classList.remove('pdp-price__badge--hidden');
      } else {
        this.badge.classList.add('pdp-price__badge--hidden');
      }
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
     REGISTRATION
     ------------------------------------------------------------------------------------------------------------------ */

  const elements = {
    'pdp-gallery': PdpGallery,
    'pdp-video': PdpVideo,
    'pdp-purchase-options': PdpPurchaseOptions,
    'pdp-offer': PdpOffer,
    'pdp-quantity': PdpQuantity,
    'pdp-cart-progress': PdpCartProgress,
    'pdp-gift-tier': PdpGiftTier,
    'pdp-price': PdpPrice
  };

  Object.keys(elements).forEach((name) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, elements[name]);
    }
  });
})();
